import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import { MetaRating } from "@/api/privateRequests/squad-builder/get-meta-ratings";
import { getEvoTreeNode } from "@/api/publicRequests/players/getEvoTreeNode";
import { getEvolutionTree } from "@/api/publicRequests/players/getEvolutionTree";
import { EvolutionsBackendTree } from "@/components/organisms/Evolutions/evolutionTreeFunctions";
import Toolbar from "@/containers/Toolbar";
import { capitalizeWordsAndRemoveDashes } from "@/functions/capitalizeFirstLetters";
import { useSticky } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useMemo, useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import {
  Chemistry,
  DetailedPlayerAction,
  detailedPlayerReducer,
} from "./reducer";
import {
  AlternativeVersion,
  AlternativeVersionType,
} from "./sections/AlternativeVersionsSection";
import { AttributesSection } from "./sections/Attributes/AttributesSection";
import { EvolutionTree } from "./sections/Evolutions/EvolutionTree";
import PlayerMainStatsSection from "./sections/MainStatsSection";
import { DetailedPlayStylesSection } from "./sections/PlayStyles/DetailedPlayStylesSection";
import { PlayStylesSection } from "./sections/PlayStyles/PlayStylesSection";
import { PlayerBio } from "./sections/PlayerBioSection";
import { RolesSection } from "./sections/RolesSection";
import {
  DetailedPlayerToolbar,
  TopSection,
} from "./sections/TopSection/TopSection";
import { AllRolesSection } from "./sections/const AllRolesSection";

type inGameAttributes = {
  [key: string]: number;
};

export type DetailedPlayerContext = {
  assetId: number;
  attributes: number[];
  resourceId: number;
  cardName: string;
  name: string;
  countryId: number;
  leagueId: number;
  clubId: number;
  canEvolve: boolean;
  rating: number;
  metalId: number;
  versionId: number;
  hasDynamicImage: boolean;
  preferredPosition: string;
  possiblePositions: string[];
  playStylesPlus: number[];
  playStyles: number[];
  playerUrl: string;
  skillMoves: 1 | 2 | 3 | 4 | 5;
  weakFoot: 1 | 2 | 3 | 4 | 5;
  preferredFoot: "left" | "right";
  metaRating: number;
  newPlayer: boolean;
  owned: boolean;
  rolesPlus: number[];
  sbcPrice: number;
  playerRoleId: number;
  evoPathId?: number;
  evolutionPath?: number[];
  isObjective?: boolean;
  isSbc?: boolean;
  price?: number;
};

interface DetailedPlayerLocationState {
  chemistryContext: Chemistry;
  chemStyleContext: number;
  p: DetailedPlayerContext;
}

export type DetailedPlayerType = DetailedPlayerContext & {
  bodyTypeId: number;
  cardType: string;
  height: number;
  weight: number;
  metaRatings: MetaRating[];
  inGameAttributes: inGameAttributes;
  playerIsBestWith: {
    chemStyleId: number;
    roleId: string;
  }[];
  alternativeVersions: AlternativeVersionType[];
};

export const GridBox = ({
  children,
  title,
  className,
  noLayout,
}: {
  children: React.ReactNode;
  title: string;
  className?: string;
  noLayout?: boolean;
}) => (
  <article
    className={cn(
      className,
      !noLayout && "bg-panel-background rounded-xl pt-2",
    )}
  >
    {!noLayout && (
      <h3 className="text-xs mobile:text-sm font-semibold text-dark-tertiary px-4 mobile:pt-1 mobile:mb-1.5">
        {title}
      </h3>
    )}
    {children}
  </article>
);

export const DetailedPlayer = () => {
  const [player, setPlayer] = useState<DetailedPlayerType>();
  const { resourceId: urlResourceId, name, version } = useParams();
  const playerName = useMemo(
    () => capitalizeWordsAndRemoveDashes(name),
    [name],
  );
  const versionName = useMemo(
    () => capitalizeWordsAndRemoveDashes(version),
    [version],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [showEvolutionTree, setShowEvolutionTree] = useState(false);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [evolutionTree, setEvolutionTree] = useState<EvolutionsBackendTree>({});
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [chemStylesOpen, setChemStylesOpen] = useState(
    localStorage.getItem("chemStylesOpen") === "true",
  );
  const [isAlternativeVersionsOpenMobile, setIsAlternativeVersionsOpenMobile] =
    useState(false);
  const location = useLocation();
  const {
    p: playerContext,
    chemistryContext = 0,
    // chemStyleContext = 250, // Think about this.
  } = (location.state as DetailedPlayerLocationState | null) ?? {};
  const topToPage = useSelector(topToPageSelector);

  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: 48,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const [state, dispatch] = useReducer(detailedPlayerReducer, {
    chemistry: {
      currentChemistry: 0,
      temporaryChemistry: undefined,
    },
    chemStyle: {
      currentChemstyleId: undefined,
      temporaryChemstyleId: undefined,
      bestChemStyleId: undefined,
      hasSelectedChemStyleManually: false,
    },
    playerRoleId:
      +searchParams.get("player-role-id") ||
      playerContext?.playerRoleId ||
      undefined,
    metaRating: {
      currentMetaRating: playerContext?.metaRating,
      temporaryMetaRating: undefined,
      metaRatingZeroChem: undefined,
    },
    metaRatingsForCurrentRole: [],
    metaRatingsForAllRoles: [],
  });
  const resourceId = useMemo(() => {
    setEvolutionTree({}); // We have to reset this when changing the player.

    setShowEvolutionTree(false);
    if (urlResourceId) {
      return parseInt(urlResourceId);
    }
    if (playerContext?.resourceId) {
      return playerContext.resourceId;
    }
    return undefined;
  }, [urlResourceId]);

  const evoPathId = useMemo(() => {
    if (searchParams.get("evo-path-id")) {
      return parseInt(searchParams.get("evo-path-id"));
    }
    if (playerContext?.evoPathId) {
      return playerContext.evoPathId;
    }
    return undefined;
  }, [playerContext?.evoPathId, searchParams]);
  const evolutionPath = useMemo(() => {
    if (
      searchParams.get("evolution-path") &&
      searchParams.get("evolution-path") !== "start"
    ) {
      return searchParams.get("evolution-path")?.split(",").map(Number);
    }
    if (playerContext?.evolutionPath) {
      return playerContext.evolutionPath;
    }
    return undefined;
  }, [playerContext?.evolutionPath, searchParams]);

  useEffect(() => {
    if (playerContext) {
      setPlayer({
        ...player,
        ...playerContext,
        resourceId: resourceId,
      });
    }
    if (chemistryContext) {
      handleAction({
        type: "SET_CHEMISTRY",
        payload: chemistryContext,
      });
    }
  }, [playerContext?.resourceId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPlayer = async () => {
      const player = await getPlayer({
        resourceId: resourceId,
        playerRoleId: state.playerRoleId,
        evoPathId: evoPathId,
        evolutionPath: evolutionPath,
        expanded: true,
      });

      handleAction({
        type: "SET_META_RATINGS_FOR_CURRENT_ROLE",
        payload: player.metaRatings,
      });
      setPlayer(player);
    };
    fetchPlayer();
  }, [playerContext, resourceId, state.playerRoleId, evoPathId]);

  const handleAction = (action: DetailedPlayerAction) => {
    dispatch(action);
  };

  const handleGetEvolutionTree = async () => {
    const evolutionTree = await getEvolutionTree({
      resourceId: resourceId,
      playerRoleId: state.playerRoleId,
      evoPathId: evoPathId,
    });
    setEvolutionTree(evolutionTree);
  };

  useEffect(() => {
    if (showEvolutionTree) {
      handleGetEvolutionTree();
    }
  }, [showEvolutionTree]);

  const handleNodeClicked = async (nodeId: string) => {
    setSearchParams({
      ...searchParams,
      "player-role-id": state.playerRoleId?.toString(),
      "evolution-path": nodeId?.replaceAll("-", ","),
      ...(evoPathId && { "evo-path-id": evoPathId.toString() }),
    });

    const evoPlayer = await getEvoTreeNode({
      resourceId,
      nodeId,
      playerRoleId: state.playerRoleId,
      evoPathId: evoPathId,
    });

    handleAction({
      type: "SET_META_RATINGS_FOR_CURRENT_ROLE",
      payload: evoPlayer.metaRatings,
    });

    setPlayer({
      ...player,
      ...evoPlayer,
    });
  };

  const handleChemStylesOpen = () => {
    localStorage.setItem("chemStylesOpen", !chemStylesOpen ? "true" : "false");
    setChemStylesOpen(!chemStylesOpen);
  };

  return (
    <main className="bg-panel-base min-h-screen pb-10">
      {isMobile && <div ref={refTopBar} className="absolute top-50" />}
      <TopSection
        name={playerName}
        versionName={versionName}
        player={player}
        metaRating={
          state.metaRating.temporaryMetaRating ??
          state.metaRating.currentMetaRating
        }
        metaRatingZeroChem={state.metaRating.metaRatingZeroChem}
        chemistry={state.chemistry.currentChemistry}
        chemStyleId={state.chemStyle.currentChemstyleId}
        playerRoleId={state.playerRoleId}
        rolesPlus={player?.rolesPlus}
        scrollPastTopBar={scrollPastTopBar}
      />
      {!isMobile && <div ref={refTopBar} className="absolute top-50" />}
      <div
        className={cn(
          scrollPastTopBar && "sticky z-20",
          scrollPastTopBar && !isTouchDevice
            ? topToPage.tailwindString
            : "top-12",
          !isMobile && "shadow-soft-dark-bottom",
        )}
      >
        <Toolbar
          color={scrollPastTopBar && isMobile ? "bg-panel-base" : "bg-toolbar"}
          children={
            <DetailedPlayerToolbar
              player={player}
              playerRoleId={state.playerRoleId}
              chemStyleId={state.chemStyle.currentChemstyleId}
              metaRating={
                state.metaRating.temporaryMetaRating ??
                state.metaRating.currentMetaRating
              }
              chemistry={state.chemistry.currentChemistry}
              metaRatingZeroChem={state.metaRating.metaRatingZeroChem}
              playerName={playerName}
              resourceId={resourceId}
              rolesPlus={player?.rolesPlus}
              scrollPastTopBar={scrollPastTopBar}
              refTopBar={refTopBar}
              setIsAlternativeVersionsOpenMobile={
                setIsAlternativeVersionsOpenMobile
              }
              isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
              showEvolutionTree={showEvolutionTree}
              setShowEvolutionTree={setShowEvolutionTree}
            />
          }
        />
      </div>
      {isAlternativeVersionsOpenMobile && (
        <div className="flex flex-row gap-x-2 px-4 py-2 bg-panel-middleground">
          {player?.alternativeVersions
            ?.sort((a, b) => a.rating - b.rating)
            .map((version) => (
              <AlternativeVersion
                key={version.resourceId}
                version={version}
                playerName={playerName}
                playerRoleId={state.playerRoleId}
                isActive={version.resourceId === resourceId}
              />
            ))}
        </div>
      )}
      {showEvolutionTree && Object.keys(evolutionTree).length > 0 && (
        <EvolutionTree
          chosenPath={[
            "start",
            ...(evolutionPath?.map((id) => id.toString()) ?? []),
          ]}
          evolutionTree={evolutionTree}
          handleNodeClicked={handleNodeClicked}
        />
      )}
      <div className="grid grid-cols-4 max-w-content mx-auto w-full gap-5 mobile:gap-4 px-4 touch-device:px-10 large-desktop:px-20">
        <div className="mobile:pb-2 col-span-4 grid grid-cols-4 gap-0.5 mobile:gap-4 pt-3 mobile:pt-6">
          <PlayerMainStatsSection
            positions={player?.possiblePositions}
            weakFoot={player?.weakFoot}
            skillMoves={player?.skillMoves}
            preferredFoot={player?.preferredFoot}
            bodyTypeId={player?.bodyTypeId}
          />
          <PlayStylesSection
            playStylesPlus={player?.playStylesPlus}
            playStyles={player?.playStyles}
          />
        </div>
        {showAllRoles ? (
          <AllRolesSection
            handleShowAllRoles={() => {
              setShowAllRoles(false);
            }}
            chemistry={state.chemistry.currentChemistry}
            currentPlayerRoleId={state.playerRoleId}
            metaRatingsForAllRoles={state.metaRatingsForAllRoles}
            rolesPlus={player?.rolesPlus}
            possiblePositions={player?.possiblePositions}
            handleAction={handleAction}
          />
        ) : (
          <>
            <RolesSection
              rolesPlus={player?.rolesPlus}
              metaRatingsAllRoles={state.metaRatingsForAllRoles}
              currentChemistry={state.chemistry.currentChemistry}
              currentPlayerRoleId={state.playerRoleId}
              handleAction={handleAction}
              handleShowAllRoles={() => {
                setShowAllRoles(true);
              }}
              positions={player?.possiblePositions}
              chemStylesOpen={chemStylesOpen}
            />
            <AttributesSection
              inGameAttributes={player?.inGameAttributes}
              overallAttributes={player?.attributes}
              isGk={player?.preferredPosition === "GK"}
              chemistry={state.chemistry}
              chemStyle={state.chemStyle}
              playerHeight={player?.height}
              handleAction={handleAction}
              chemStylesOpen={chemStylesOpen}
              handleChemStylesOpen={handleChemStylesOpen}
            />
          </>
        )}
        <PlayerBio
          displayName={player?.name}
          countryId={player?.countryId}
          clubId={player?.clubId}
          leagueId={player?.leagueId}
          possiblePositions={player?.possiblePositions}
          cardType={player?.cardType}
          versionId={player?.versionId}
          bodyTypeId={player?.bodyTypeId}
          skillMoves={player?.skillMoves}
          weakFoot={player?.weakFoot}
          preferredFoot={player?.preferredFoot}
          weight={player?.weight}
          height={player?.height}
        />
        <DetailedPlayStylesSection
          playStylesPlus={player?.playStylesPlus}
          playStyles={player?.playStyles}
          isGk={player?.preferredPosition === "GK"}
        />
      </div>
    </main>
  );
};
